import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@workos-inc/authkit-react";
import * as Sentry from "@sentry/react";
import { apiFetchMultipart } from "@/lib/api";
import { uploadImageFilename } from "@/lib/upload-filename";
import { recordKeys } from "@/lib/query-keys";
import { invalidateReportContents } from "@/lib/invalidate-report-contents";
import {
  updateCachedRecord,
  type RecordCacheEntry,
} from "@/lib/records/record-cache";
import { normalizeRecordImageShape } from "@/offline/record-shape";
import { seedProvisionalAsset } from "@/offline/asset-backfill";
import { thumbAssetKeyFor } from "@/offline/asset-keys";
import type { ProjectRecord } from "@/types/api";
import type { Drawing } from "@/components/records/image-markup-editor/types";
import type { CropRect } from "@/components/records/image-markup-editor/crop-overlay";

export interface UpdateRecordImageVars {
  projectId: string;
  recordId: string;
  /** The tapped photo's server image id (the v2.0 `images[].id`). */
  imageId: string;
  /** Fresh client-rendered annotated bytes — the multipart `image` field. */
  annotatedBlob: Blob;
  /**
   * The ~512px companion the editor rendered in the same pass. Never uploaded
   * — it exists so the edited photo can be painted and cached locally without
   * waiting for the server to regenerate its derivative.
   */
  thumbnailBlob: Blob;
  /**
   * The PRISTINE raw source bytes — the multipart `original` field. Never the
   * editor's transcoded working copy (see [[heic-conversion]]): re-editing must
   * not replace an untouched original (and its EXIF) with a re-encoded JPEG.
   */
  originalFile: File;
  /**
   * The full annotation payload. Serialized to the same camelCase wire shape
   * every other v2.0 writer sends (`buildMultiPhotoFormData` /
   * `buildAppendFormData`): `{drawings, crop, rotation, downscaleMaxPx}`.
   * `rotation` is ALWAYS carried — the retired record-level markup PATCH
   * dropped it, which mis-seeded the next re-edit of a rotated photo.
   */
  annotations: {
    drawings: Drawing[];
    crop?: CropRect;
    rotation?: number;
    downscaleMaxPx?: number;
  };
}

/**
 * Per-photo markup re-edit for a SYNCED record (US-003c, online-only) — the
 * web consumer of the v2.0 per-photo markup endpoint that shipped as mobile
 * M7's ride-along:
 *
 * `PATCH /projects/2.0/projects/{projectId}/records/{recordId}/images/{imageId}/`
 *
 * Multipart `image` (fresh annotated bytes) + `original` (pristine source
 * bytes) + `annotations` (JSON). Sending `image` + `original` together makes
 * the server regenerate that photo's thumbnail / report_image; `annotations`
 * overwrites the photo's stored payload opaquely.
 *
 * The caller does not wait on this: the editor closes and the tile repaints
 * from `thumbnailBlob` the moment Done is pressed, and this PATCH runs behind
 * that (see [[image-viewer-modal]]). Nothing is rolled back on failure — the
 * bytes were never destructive to begin with — so the recovery is a Retry that
 * re-sends the same payload rather than an undo.
 *
 * Because the modal can be closed while this is in flight, the cache reconcile,
 * the asset seed and the failure report all live in the mutation's OWN
 * `onSuccess` / `onError` here — `mutate()`-level callbacks are discarded when
 * the calling component unmounts.
 *
 * On success the response (the FULL updated v2.0 record) is normalized
 * (`normalizeRecordImageShape` — cached records carry the canonical
 * `{image, thumbnail}` shape) and spliced over the record in every matching
 * records-list cache, then the list is invalidated so the regenerated
 * derivative URLs / `updated_at` cache-bust keys settle authoritatively.
 *
 * The hook owns no UI — the caller passes `onError` / `onSuccess` via the
 * standard mutate callbacks per [[mutations]]. Mutations keep the global
 * `retry: false`: this PATCH is not idempotent (double-apply re-renders
 * derivatives twice), so retry stays a user action inside the editor.
 */
export function useUpdateRecordImage() {
  const { getAccessToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<ProjectRecord, Error, UpdateRecordImageVars>({
    mutationFn: async ({
      projectId,
      recordId,
      imageId,
      annotatedBlob,
      originalFile,
      annotations,
    }) => {
      const formData = new FormData();
      formData.append("image", annotatedBlob, "image.jpg");
      // The server validates uploads by filename extension — route the name
      // through the shared helper so it always carries one (see the
      // 2026-08-18 extensionless-name regression in [[image-viewer-modal]]).
      formData.append(
        "original",
        originalFile,
        uploadImageFilename(originalFile.name, originalFile.type),
      );
      formData.append(
        "annotations",
        JSON.stringify({
          drawings: annotations.drawings,
          crop: annotations.crop,
          rotation: annotations.rotation,
          downscaleMaxPx: annotations.downscaleMaxPx,
        }),
      );
      const updated = await apiFetchMultipart<ProjectRecord>(
        `/projects/2.0/projects/${projectId}/records/${recordId}/images/${imageId}/`,
        formData,
        getAccessToken,
        { method: "PATCH" },
      );
      return normalizeRecordImageShape(updated);
    },
    onSuccess: (updated, { projectId, imageId, thumbnailBlob }) => {
      // Seed the edited photo's POST-edit cache key with the bytes we already
      // hold. The key is versioned by `updated_at`, which this PATCH just
      // advanced — so without a seed the invalidation below hands the tile a
      // brand-new key whose derivative the server may still be regenerating,
      // and the records grid visibly lags the edit that produced it. Marked
      // provisional, so backfill upgrades it to the server's own thumbnail.
      // Never throws; deliberately not awaited (the in-memory prime that makes
      // the tile resolve is synchronous inside).
      void seedProvisionalAsset(
        thumbAssetKeyFor(imageId, updated.updated_at),
        projectId,
        thumbnailBlob,
      );
      // Splice the authoritative post-edit record over every cached copy —
      // each list page (one cache per filter combination) and the record's own
      // entry — so the edited photo's fresh `images[]` URLs + annotations are
      // readable immediately.
      const queries = queryClient.getQueriesData<RecordCacheEntry>({
        queryKey: recordKeys.byProject(projectId),
      });
      for (const [key] of queries) {
        queryClient.setQueryData<RecordCacheEntry>(key, (old) =>
          updateCachedRecord(old, updated.id, () => updated),
        );
      }
    },
    onError: (error, { projectId, recordId, imageId }) => {
      // The editor has already closed by now, so an unreported failure here is
      // invisible data loss — the user's markups exist only in the retained
      // payload behind the modal's Retry. Lives in the mutation's own onError
      // so closing the modal mid-flight cannot swallow it.
      console.error("Update record image markups failed:", error);
      Sentry.captureException(error, {
        level: "error",
        tags: { area: "records", phase: "markup-patch" },
        contexts: { record: { projectId, recordId, imageId } },
      });
    },
    onSettled: (_data, _err, { projectId }) => {
      // Refetch the authoritative list — regenerated derivatives and the
      // record's advanced `updated_at` (the thumbnail cache-bust key) settle
      // here. Project caches carry no markup state, so they are not touched.
      void queryClient.invalidateQueries({
        queryKey: recordKeys.byProject(projectId),
      });
      // A report's tile shows the photo's thumbnail too.
      invalidateReportContents(queryClient, projectId);
    },
  });
}
